import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  TREE_NODE,
  TREE_TYPE,
  TREE_VIEW_BY,
  DATA_HEALTH_DAYS_BACK,
  DATA_HEALTH_FILTER_TYPE,
  DATA_HEALTH_FILTERS,
  INCIDENT_LIST_FILTERS,
  LINEAGE_FILTERS,
  METRICS_TABLE_FILTERS,
  TEST_RESULTS_TABLE_FILTERS,
  TEST_RESULTS_GROUP_BY,
  TEST_MANAGEMENT_TABLES_TABLE_FILTERS,
  TEST_MANAGEMENT_COLUMNS_TABLE_FILTERS,
  TEST_MANAGEMENT_CUSTOM_QUERY_TABLE_FILTERS,
  TEST_MANAGEMENT_PRS_TABLE_FILTERS,
  MODEL_RUNS_TABLE_FILTERS,
  TEST_RUNS_TABLE_FILTERS,
  VIEW_ID,
  DAYS_BACK,
} from '../constants/urlParams';
import useSelectedEnv from './useSelectedEnv';

// Params to persist in each route. Meaning if this param is added in a specific route,
// when navigating away and back to the route in will be re-added.
const PARAMS_TO_PERSIST = [
  TREE_NODE,
  TREE_TYPE,
  TREE_VIEW_BY,
  DATA_HEALTH_DAYS_BACK,
  DATA_HEALTH_FILTER_TYPE,
  DATA_HEALTH_FILTERS,
  INCIDENT_LIST_FILTERS,
  LINEAGE_FILTERS,
  METRICS_TABLE_FILTERS,
  TEST_RESULTS_TABLE_FILTERS,
  TEST_RESULTS_GROUP_BY,
  TEST_MANAGEMENT_TABLES_TABLE_FILTERS,
  TEST_MANAGEMENT_COLUMNS_TABLE_FILTERS,
  TEST_MANAGEMENT_CUSTOM_QUERY_TABLE_FILTERS,
  TEST_MANAGEMENT_PRS_TABLE_FILTERS,
  MODEL_RUNS_TABLE_FILTERS,
  TEST_RUNS_TABLE_FILTERS,
  VIEW_ID,
  DAYS_BACK,
];

// Routes in which the tree view is rendered in.
const NAV_TREE_ROUTES = [
  '/catalog',
  '/catalog/configuration',
  '/catalog/coverage',
  '/metrics',
  '/test-management',
  '/test-management/configuration',
  '/test-management/alerts',
  '/report/lineage',
  '/report/test-results',
  '/report/model-runs',
  '/report/test-runs',
  '/report/dashboard',
  '/data-health',
  '/incidents',
];

// Params that need to be shared between routes, meaning if they are changed in one route, they will also pass to another route when navigated to.
// The key is the url param itself, and the value is a list of routes that the param should be shared between.
const SHARED_URL_PARAMS: Record<string, string[]> = {
  [TREE_NODE]: NAV_TREE_ROUTES,
  [TREE_TYPE]: NAV_TREE_ROUTES,
  [TREE_VIEW_BY]: NAV_TREE_ROUTES,
  [VIEW_ID]: NAV_TREE_ROUTES,
};

const filterPersistedParams = (params: URLSearchParams | string) => {
  const parsedParams = new URLSearchParams(params);
  const filteredParams = new URLSearchParams(params);

  [...parsedParams.keys()].forEach((key) => {
    if (!PARAMS_TO_PERSIST.includes(key)) {
      filteredParams.delete(key);
    }
  });

  return filteredParams;
};

const usePersistSearchParams = () => {
  const { pathname: pathWithEnvid, search } = useLocation();
  const envid = useSelectedEnv();
  const [paramStore, setParamStore] = useState<Record<string, string>>({});

  const stripEnvid = useCallback(
    (path: string) => path.replace(`/${envid}`, ''),
    [envid],
  );

  const pathname = useMemo(
    () => stripEnvid(pathWithEnvid),
    [pathWithEnvid, stripEnvid],
  );

  const getPathWithStoredParams = useCallback(
    (to: string) => {
      const [toPathname, toSearch] = to.split('?');
      const toPathNoEnv = stripEnvid(toPathname);

      const storedSearch = paramStore[toPathNoEnv];
      const searchParams = toSearch || storedSearch || '';

      return `${toPathname}?${searchParams}`;
    },
    [stripEnvid, paramStore],
  );

  const updateSharedRoutesStorage = useCallback(() => {
    const sharedParamKeys = Object.keys(SHARED_URL_PARAMS);
    const sharedRoutes = Object.values(SHARED_URL_PARAMS).flat();
    const shouldUpdate = sharedRoutes.some((route) => route === pathname);

    if (shouldUpdate) {
      setParamStore((prevStore) => {
        const updatedStore = { ...prevStore };
        sharedParamKeys.forEach((key) => {
          const routesToUpdate = SHARED_URL_PARAMS[key];
          routesToUpdate.forEach((route) => {
            const storedSearch = new URLSearchParams(updatedStore[route] || '');
            const currentSearch = new URLSearchParams(search);
            const valueToSet = currentSearch.get(key);

            if (valueToSet) {
              storedSearch.set(key, valueToSet);
              updatedStore[route] = storedSearch.toString();
            } else if (storedSearch.has(key)) {
              storedSearch.delete(key);
              updatedStore[route] = storedSearch.toString();
            }
          });
        });
        return updatedStore;
      });
    }
  }, [pathname, search]);

  const updateCurrentRouteStorage = useCallback(() => {
    const persistedParams = filterPersistedParams(search).toString();

    setParamStore((prevStore) => {
      const updatedStore = { ...prevStore };
      if (persistedParams) {
        updatedStore[pathname] = persistedParams;
      } else {
        delete updatedStore[pathname];
      }
      return updatedStore;
    });
  }, [pathname, search]);

  useEffect(() => {
    updateCurrentRouteStorage();
    updateSharedRoutesStorage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return { getPathWithStoredParams };
};

export default usePersistSearchParams;
